import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Popover,
  Tooltip,
  TextField,
  Button,
} from "@mui/material";
import { getMemberDispositions } from "../../api/disposition";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { formatTimeOnly } from "../../utils/formatDateTime";
import NotesIcon from "../../assets/note-icon.svg";

const HEADERS = [
  { label: "Date & Time", width: "11%" },
  { label: "Care Guide", width: "14%" },
  { label: "Program", width: "15%" },
  { label: "Medication", width: "18%" },
  { label: "Outreach Method", width: "12%" },
  { label: "Outreach Outcome", width: "25%" },
  { label: "", width: "5%" },
];

const fixDateString = (s) => {
  if (typeof s !== "string") return "";
  return s.replace(/(\.\d{3})\d+$/, "$1");
};

const formatDate = (dateValue) => {
  if (dateValue == null || dateValue === "") return "N/A";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fixed = fixDateString(String(dateValue));
    const utcString = fixed.endsWith("Z") ? fixed : fixed + "Z";
    const d = new Date(utcString);
    if (Number.isNaN(d.getTime())) return dateValue;
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: tz,
    });
  } catch (e) {
    return e;
  }
};

const OutreachHistoryPanel = ({ memberId, onLoadingChange, onCountChange }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [historyNoteAnchor, setHistoryNoteAnchor] = useState(null);
  const [activeHistoryNote, setActiveHistoryNote] = useState("");
  const [activeHistoryId, setActiveHistoryId] = useState(null);

  useEffect(() => {
    if (typeof onLoadingChange === "function") {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    return () => {
      if (typeof onLoadingChange === "function") {
        onLoadingChange(false);
      }
    };
  }, [onLoadingChange]);

  useEffect(() => {
    const run = async () => {
      if (!memberId) return;
      try {
        setLoading(true);
        setError("");
        const data = await getMemberDispositions(memberId);
        const historyData = Array.isArray(data) ? data : [];
        setHistory(historyData);
        if (typeof onCountChange === "function") {
          onCountChange(historyData.length);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load outreach history");
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [memberId, onCountChange]);

  const handleHistoryNoteClick = (event, content, id) => {
    setHistoryNoteAnchor(event.currentTarget);
    setActiveHistoryNote(content || "");
    setActiveHistoryId(id);
  };

  const handleHistoryNoteClose = () => {
    setHistoryNoteAnchor(null);
    setActiveHistoryNote("");
    setActiveHistoryId(null);
  };

  const CELL_BORDER = {
    borderColor: "#E8E8E9",
    borderWidth: "0px 0.5px 0px 0.5px",
    borderStyle: "solid",
  };

  let rowsContent;
  if (loading) {
    rowsContent = (
      <TableRow>
        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
          <Typography sx={{ color: "#66638C", fontSize: "14px" }}>
            Loading outreach history...
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else if (error) {
    rowsContent = (
      <TableRow>
        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
          <Typography sx={{ color: "#CC0000", fontSize: "14px" }}>
            {error}
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else if (history.length === 0) {
    rowsContent = (
      <TableRow>
        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
          <Typography sx={{ color: "#66638C", fontSize: "14px" }}>
            No outreach history available.
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else {
    rowsContent = history.map((item, index) => {
      const dateStr = item.updatedAt;
      const outcomeColor = item?.colourCode || "#403C6F";
      const outreachMethod =
        item.outreachMethodDisplayName ||
        item.outreach_method ||
        item.contactMethod ||
        item.contact_method ||
        item.method ||
        "";
      const rawNote =
        item.note ||
        item.notes ||
        item.dispositionNotes ||
        item.disposition_notes ||
        item.descriptions ||
        item.details ||
        "";
      const noteStr = String(rawNote).trim();
      const subCategoryFull = String(item?.subCategoryDisplayName || "").trim();
      const hasSubCategory = Boolean(subCategoryFull);
      const outcomeDisplayText = item?.outcomeDisplayName || "";
      const isNoteActive =
        historyNoteAnchor &&
        activeHistoryId === (item.id || `history-${index}`);

      return (
        <TableRow
          key={item.id || `history-${index}`}
          sx={{
            "&:hover": { backgroundColor: "#F7F8FB" },
            "& td": { borderBottom: "1px solid #E0E0E0" },
          }}
        >
          {/* Date & Time */}
          <TableCell
            sx={{
              color: "#403C6F",
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
              ...CELL_BORDER,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <span>{formatDate(dateStr)}</span>
              <span
                style={{ color: "#66638C", fontSize: "12px", fontWeight: 400 }}
              >
                {formatTimeOnly(dateStr)}
              </span>
            </Box>
          </TableCell>

          {/* Care Guide */}
          <TableCell
            sx={{
              color: "#403C6F",
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
              ...CELL_BORDER,
            }}
          >
            {item.careguideName || "N/A"}
          </TableCell>

          {/* Program */}
          <TableCell
            sx={{
              color: "#403C6F",
              fontWeight: 600,
              fontSize: "13px",
              whiteSpace: "nowrap",
              ...CELL_BORDER,
            }}
          >
            {item.programName || "N/A"}
          </TableCell>

          {/* Medication */}
          <TableCell
            sx={{
              color: "#403C6F",
              fontWeight: 400,
              fontSize: "13px",
              verticalAlign: "top",
              ...CELL_BORDER,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Box
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {capitalizeFirstLetter(
                  item.drugName ||
                    item.drug_name ||
                    item.medicationName ||
                    item.medication_name ||
                    "",
                )}
              </Box>
              {(item.drugNdc || item.drug_ndc || item.ndc) && (
                <Box
                  sx={{
                    fontSize: "11px",
                    color: "#66638C",
                    whiteSpace: "nowrap",
                  }}
                >
                  NDC:{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: "#403C6F" }}
                  >
                    {item.drugNdc || item.drug_ndc || item.ndc}
                  </Box>
                </Box>
              )}
            </Box>
          </TableCell>

          {/* Outreach Method */}
          <TableCell
            sx={{
              color: "#403C6F",
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
              ...CELL_BORDER,
            }}
          >
            {outreachMethod === "SMS"
              ? outreachMethod
              : capitalizeFirstLetter(outreachMethod)}
          </TableCell>

          {/* Outreach Outcome */}
          <TableCell
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              width: "25%",
              color: outcomeColor,
              ...CELL_BORDER,
            }}
          >
            {hasSubCategory ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "2px",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: outcomeColor,
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {capitalizeFirstLetter(outcomeDisplayText)}
                </span>
                <Tooltip title={subCategoryFull} arrow placement="top">
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: "12px",
                      color: "#403C6F",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {` (${subCategoryFull})`}
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <Box
                sx={{
                  whiteSpace: "nowrap",
                  color: outcomeColor,
                }}
              >
                {capitalizeFirstLetter(outcomeDisplayText)}
              </Box>
            )}
          </TableCell>

          {/* Notes */}
          <TableCell
            align="center"
            sx={{
              ...CELL_BORDER,
              zIndex: isNoteActive ? 2201 : "auto",
              position: isNoteActive ? "relative" : "static",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isNoteActive
                    ? "0 0 10px rgba(64,60,111,0.6)"
                    : "none",
                  transition: "all 0.3s ease-in-out",
                }}
              >
                <IconButton
                  size="small"
                  disabled={!noteStr}
                  onClick={(e) =>
                    handleHistoryNoteClick(
                      e,
                      noteStr,
                      item.id || `history-${index}`,
                    )
                  }
                  sx={{
                    padding: "4px",
                    color: "#433ba0ff",
                    "&:hover": { backgroundColor: "rgba(67, 59, 160, 0.08)" },
                    "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed" },
                  }}
                >
                  <img
                    src={NotesIcon}
                    alt="Notes"
                    style={{
                      width: "18px",
                      height: "18px",
                      opacity: noteStr ? 1 : 0.9,
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  }

  return (
    <Box
      sx={{
        mt: 1,
        flex: 1,
        minHeight: 0,
        height: "100%",
        pb: 3,
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: "8px",
          border: "1px solid #E8E8E9",
          overflowY: "auto",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F5F5F5",
            borderRadius: "4px",
            margin: "4px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#A7A5BD",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#8E8CA8",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#A7A5BD #F5F5F5",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CFCEDA",
                position: "sticky",
                top: 0,
                zIndex: 5000,
                boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              {HEADERS.map((header) => (
                <TableCell
                  key={header.label}
                  sx={{
                    backgroundColor: "#CFCEDA",
                    color: "#403C6F",
                    fontWeight: 700,
                    fontSize: "14px",
                    py: 1.5,
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                    width: header.width,
                    position: "sticky",
                    top: 0,
                    zIndex: 5000,
                    borderColor: "#f5f5f8ff",
                    borderWidth: "0px 1px 0px 1px",
                    borderStyle: "solid",
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{rowsContent}</TableBody>
        </Table>
      </TableContainer>
      <Popover
        open={Boolean(historyNoteAnchor)}
        anchorEl={historyNoteAnchor}
        onClose={handleHistoryNoteClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          backdrop: {
            invisible: false,
            sx: { backgroundColor: "rgba(27, 27, 27, 0.3)" },
          },
          paper: {
            sx: {
              p: 2,
              mt: 0.4,
              width: 350,
              backgroundColor: "#F7F8FB",
              height: "290px",
              zIndex: 1000,
              borderRadius: "12px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <TextField
            multiline
            minRows={2}
            value={activeHistoryNote || ""}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "#FFFFFF",
              width: "100%",
              minHeight: "200px",
              borderRadius: "8px",
              boxShadow: "0px 0px 2px 0px #00000040",
              overflow: "auto",
              "& .MuiOutlinedInput-root": {
                height: "100%",
                alignItems: "flex-start",
                padding: 0,
                "& textarea": {
                  padding: "16px",
                  boxSizing: "border-box",
                  overflow: "auto !important",
                  maxHeight: "calc(200px - 32px)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
            slotProps={{ input: { readOnly: true } }}
          />
        </Box>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: 1, gap: 1 }}
        >
          <Button
            onClick={handleHistoryNoteClose}
            variant="outlined"
            sx={{
              height: 38,
              borderRadius: "52px",
              border: "1px solid #9F9DB6",
              color: "#403C6F",
            }}
          >
            Close
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

OutreachHistoryPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLoadingChange: PropTypes.func,
  onCountChange: PropTypes.func,
};

export default OutreachHistoryPanel;
